import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { authApi } from "../api/auth";

// ── Helpers ───────────────────────────────────────────────────────────────────

function getInitials(name: string | null | undefined, email: string | null | undefined): string {
  if (name) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return ((parts[0][0] ?? "") + (parts[parts.length - 1][0] ?? "")).toUpperCase();
    if (parts[0]) return parts[0].slice(0, 2).toUpperCase();
  }
  if (email) return email.slice(0, 2).toUpperCase();
  return "SV";
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function IconDashboard() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}
function IconTransfers() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}
function IconVessels() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="5" r="2" /><line x1="12" y1="7" x2="12" y2="13" />
      <path d="M5 13h14l-1.5 5a2 2 0 0 1-2 1.5H8.5a2 2 0 0 1-2-1.5L5 13Z" />
    </svg>
  );
}
function IconContacts() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}
function IconMessages() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}
function IconAudit() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
      <rect x="9" y="3" width="6" height="4" rx="1" /><path d="M9 12h6M9 16h4" />
    </svg>
  );
}
function IconSettings() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
    </svg>
  );
}
function IconLogout() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}
function IconMenu() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}
function IconClose() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

// ── Nav config ────────────────────────────────────────────────────────────────

const NAV_GROUPS = [
  {
    label: "Main",
    items: [
      { to: "/dashboard", label: "Dashboard", icon: <IconDashboard /> },
      { to: "/transfers", label: "Transfers", icon: <IconTransfers /> },
      { to: "/vessels",   label: "Vessels",   icon: <IconVessels /> },
      { to: "/contacts",  label: "Contacts",  icon: <IconContacts /> },
    ],
  },
  {
    label: "Compliance",
    items: [
      { to: "/messages", label: "Messages", icon: <IconMessages /> },
      { to: "/audit",    label: "Audit Log", icon: <IconAudit />, adminOnly: true },
    ],
  },
  {
    label: "Account",
    items: [
      { to: "/settings", label: "Settings", icon: <IconSettings /> },
    ],
  },
];

// ── Sidebar ───────────────────────────────────────────────────────────────────

interface SidebarProps {
  pathname: string;
  isAdmin: boolean;
  displayName: string;
  initials: string;
  role: string;
  onLogout: () => void;
  onClose?: () => void;
}

function Sidebar({ pathname, isAdmin, displayName, initials, role, onLogout, onClose }: SidebarProps) {
  return (
    <div className="flex flex-col h-full w-[232px] bg-navy-950" style={{ borderRight: "1px solid rgba(255,255,255,0.06)" }}>
      {/* Brand */}
      <div className="flex items-center justify-between px-5 py-[18px] shrink-0">
        <Link to="/dashboard" className="flex items-center gap-2.5" onClick={onClose}>
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-300">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="16" /><line x1="8" y1="12" x2="16" y2="12" />
            <line x1="12" y1="2" x2="12" y2="4" /><line x1="12" y1="20" x2="12" y2="22" />
            <line x1="2" y1="12" x2="4" y2="12" /><line x1="20" y1="12" x2="22" y2="12" />
          </svg>
          <span className="text-white font-bold text-[15px] tracking-tight">SeaVault</span>
        </Link>
        {onClose && (
          <button onClick={onClose} className="text-white/40 hover:text-white/70 transition-colors">
            <IconClose />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 pb-4 space-y-5">
        {NAV_GROUPS.map((group) => {
          const items = group.items.filter((item) =>
            !("adminOnly" in item && item.adminOnly && !isAdmin)
          );
          if (items.length === 0) return null;
          return (
            <div key={group.label}>
              <p
                className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-widest"
                style={{ color: "rgba(255,255,255,0.28)" }}
              >
                {group.label}
              </p>
              {items.map((item) => {
                const active = pathname === item.to;
                return (
                  <NavItem
                    key={item.to}
                    to={item.to}
                    label={item.label}
                    icon={item.icon}
                    active={active}
                    onClick={onClose}
                  />
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* User */}
      <div className="shrink-0 px-4 py-4" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
        <div className="flex items-center gap-2.5">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0"
            style={{ backgroundColor: "#1d5199" }}
          >
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate leading-tight">{displayName}</p>
            <p className="text-[11px] leading-tight mt-0.5 truncate" style={{ color: "rgba(255,255,255,0.38)" }}>
              {role === "admin" ? "Administrator" : "User"}
            </p>
          </div>
          <button
            onClick={onLogout}
            title="Sign out"
            className="shrink-0 transition-colors"
            style={{ color: "rgba(255,255,255,0.38)" }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = "rgba(255,255,255,0.75)"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = "rgba(255,255,255,0.38)"; }}
          >
            <IconLogout />
          </button>
        </div>
      </div>
    </div>
  );
}

function NavItem({ to, label, icon, active, onClick }: {
  to: string;
  label: string;
  icon: React.ReactNode;
  active: boolean;
  onClick?: () => void;
}) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors mb-0.5"
      style={{
        color: active ? "#ffffff" : "rgba(255,255,255,0.58)",
        backgroundColor: active ? "rgba(255,255,255,0.09)" : "transparent",
        borderLeft: active ? "2px solid #60a5fa" : "2px solid transparent",
      }}
      onMouseEnter={(e) => {
        if (!active) (e.currentTarget as HTMLElement).style.backgroundColor = "rgba(255,255,255,0.05)";
      }}
      onMouseLeave={(e) => {
        if (!active) (e.currentTarget as HTMLElement).style.backgroundColor = "transparent";
      }}
    >
      <span style={{ opacity: active ? 1 : 0.65 }}>{icon}</span>
      {label}
    </Link>
  );
}

// ── Layout ────────────────────────────────────────────────────────────────────

interface UserInfo { email: string; name: string | null; role: string; }

export default function Layout({ children }: { children: React.ReactNode }) {
  const { user, token, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [meUser, setMeUser] = useState<UserInfo | null>(null);

  // Fetch user on mount if hook hasn't populated it yet (page refresh)
  useEffect(() => {
    if (!user && token) {
      authApi.me().then(setMeUser).catch(() => {});
    }
  }, [user, token]);

  const displayUser = user ?? meUser;
  const initials    = getInitials(displayUser?.name, displayUser?.email);
  const displayName = displayUser?.name ?? displayUser?.email ?? "SeaVault";
  const role        = displayUser?.role ?? "";
  const isAdmin     = role === "admin";

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  const sidebarProps: SidebarProps = {
    pathname: location.pathname,
    isAdmin,
    displayName,
    initials,
    role,
    onLogout: handleLogout,
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      {/* Desktop sidebar */}
      <div className="hidden md:flex shrink-0">
        <Sidebar {...sidebarProps} />
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <Sidebar {...sidebarProps} onClose={() => setMobileOpen(false)} />
          <div className="flex-1 bg-black/50" onClick={() => setMobileOpen(false)} />
        </div>
      )}

      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile top bar */}
        <div className="md:hidden shrink-0 flex items-center gap-4 px-4 py-3 bg-navy-950">
          <button onClick={() => setMobileOpen(true)} className="text-white">
            <IconMenu />
          </button>
          <span className="text-white font-bold text-sm">SeaVault</span>
        </div>

        {children}
      </div>
    </div>
  );
}
